import { effect, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATE_CONFIG_TOKEN } from '@/core/util/translate.token';
import { Language, LANGUAGES } from '@/core/util/languages.constant';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

const LANGUAGE_KEY = `${environment.appKeyPrefix}:language`;

@Injectable({ providedIn: 'root' })
export class AppI18nService {
    private _currentLang = signal('en');
    readonly currentLang = this._currentLang.asReadonly();
    readonly languages = signal<Language[]>(LANGUAGES);
    private translate = inject(TranslateService);
    private config = inject(TRANSLATE_CONFIG_TOKEN);

    constructor() {
        effect(() => {
            if (this.config.enabled) {
                this.translate.use(this._currentLang());
            }
        });
    }

    // ✅ New async init — called by APP_INITIALIZER
    async initAsync(): Promise<void> {
        if (!this.config.enabled) return;

        this.translate.addLangs(this.config.supportedLangs);

        const lang = this.getSavedLang() ?? this.config.defaultLang;
        this.translate.setFallbackLang(this.config.defaultLang);

        // ✅ Await translation file to fully load before app starts
        await firstValueFrom(this.translate.use(lang));

        this._currentLang.set(lang);
        this.saveLang(lang);
    }

    switchLanguage(lang: string): void {
        if (this.config.enabled && this.config.supportedLangs.includes(lang)) {
            this._currentLang.set(lang);
            this.saveLang(lang);
        }
    }

    get activeLang(): string {
        return this._currentLang();
    }

    private getSavedLang(): string | null {
        const lang = localStorage.getItem(LANGUAGE_KEY);
        return this.config.supportedLangs.includes(lang ?? '') ? lang : null;
    }

    private saveLang(lang: string): void {
        localStorage.setItem(LANGUAGE_KEY, lang);
    }
}
